import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ModalDialog, { ModalCloseHandler } from 'src/components/ModalDialog';
import FieldsSet from 'src/components/FieldsSet';
import {
  SiteDeviceNodesItem,
  SiteDeviceNodesItemField,
} from 'src/pages/Sites/types';

const mainFields = [
  {
    id: SiteDeviceNodesItemField.GpuProduct,
    label: 'GPU Product',
  },
  {
    id: SiteDeviceNodesItemField.CudaDriverVersion,
    label: 'CUDA Driver Version',
  },
  {
    id: SiteDeviceNodesItemField.CudaRuntimeVersion,
    label: 'CUDA Runtime Version',
  },
];

type Props = {
  open: boolean;
  onClose: ModalCloseHandler;
  item?: SiteDeviceNodesItem;
};

// Older payloads only have raw fields; fall back to them when formatted values are absent.
function getDisplayItem(item?: SiteDeviceNodesItem): SiteDeviceNodesItem | undefined {
  if (!item) return item;
  return {
    ...item,
    cpuCoresFormatted: item.cpuCoresFormatted ?? item.cpuCores?.toString(),
    memoryFormatted: item.memoryFormatted ?? item.memoryKi,
    diskSizeFormatted: item.diskSizeFormatted ?? item.diskEphemeral,
    gpuCountFormatted: item.gpuCountFormatted ?? item.gpuCount?.toString(),
    gpuMemoryFormatted:
      item.gpuMemoryFormatted ??
      (item.gpuMemoryGiB !== undefined ? `${item.gpuMemoryGiB} GiB` : undefined),
  };
}

const headerStyle = { fontSize: '16px', fontWeight: 700 };
const rowStyle = { fontSize: '16px' };
const defaultValue = 'NA';

function NodeDetailsViewModal({ open, onClose, item }: Props) {
  const displayItem = getDisplayItem(item);
  const hasGpu = !!displayItem?.gpuCountFormatted && displayItem.gpuCountFormatted !== '0';

  return (
    <ModalDialog
      title={`GPU/CPU Details${item?.hostName ? ` — ${item.hostName}` : ''}`}
      open={open}
      onClose={onClose}
      onConfirm={onClose}
      showCancelBtn={false}
      confirmBtnText="Close"
      maxWidth="md"
      fullWidth
    >
      <FieldsSet
        data={displayItem}
        fields={mainFields}
        sx={{
          wordBreak: 'break-word',
          gap: 2,
          maxWidth: '100%',
        }}
      />

      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          rowGap: 1.5,
          columnGap: 4, 
        }}
      >
        <Typography sx={headerStyle}>Resources</Typography>
        <Typography sx={headerStyle}>Utilization</Typography>

        <Typography sx={rowStyle}>
          CPU: {displayItem?.cpuCoresFormatted ? `${displayItem.cpuCoresFormatted} cores` : defaultValue}
        </Typography>
        <Typography sx={rowStyle}>CPU: {displayItem?.cpuUtilized ?? defaultValue}</Typography>

        <Typography sx={rowStyle}>Mem: {displayItem?.memoryFormatted ?? defaultValue}</Typography>
        <Typography sx={rowStyle}>Mem: {displayItem?.memoryUtilized ?? defaultValue}</Typography>

        <Typography sx={rowStyle}>Disk: {displayItem?.diskSizeFormatted ?? defaultValue}</Typography>
        <Typography sx={rowStyle}>Disk: {displayItem?.diskUtilized ?? defaultValue}</Typography>

        {hasGpu && (
          <>
            <Typography sx={rowStyle}>GPU: {displayItem?.gpuCountFormatted ?? defaultValue}</Typography>
            <Typography sx={rowStyle}>GPU: {displayItem?.gpuCoreUtilized ?? defaultValue}</Typography>

            <Typography sx={rowStyle}>GPU Mem: {displayItem?.gpuMemoryFormatted ?? defaultValue}</Typography>
            <Typography sx={rowStyle}>GPU Mem: {displayItem?.gpuMemoryUtilized ?? defaultValue}</Typography>
          </>
        )}
      </Box>
    </ModalDialog>
  );
}

export default NodeDetailsViewModal;
